import { useState } from 'react'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { HoverableImage } from '@/components/ui/hoverable-image'
import { Check, Wand2 } from 'lucide-react'
import { TrashIcon } from '@/components/icons'

const DeleteButton = ({ image, onDelete }) => {
    return (
        <button
            onClick={() => onDelete(image.id)}
            className="tw:absolute tw:top-1 tw:right-1 tw: tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:text-xs"
        >
            <div className="tw:size-6 tw:bg-gray-300/50 tw:hover:bg-gray-300 tw:rounded-full tw:flex tw:items-center tw:justify-center">
                <TrashIcon size={16} />
            </div>
        </button>
    )
}

const RefineButton = ({ onRefine }) => {
    return (
        <button
            type="button"
            aria-label="Refine image"
            onClick={(e) => {
                e.stopPropagation()
                onRefine()
            }}
            className="tw:absolute tw:top-1 tw:right-8 tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:text-xs"
        >
            <div className="tw:size-6 tw:bg-gray-300/50 tw:hover:bg-gray-300 tw:rounded-full tw:flex tw:items-center tw:justify-center">
                <Wand2 size={14} />
            </div>
        </button>
    )
}

const SelectionIcon = () => {
    return (
        <div className="tw:absolute tw:top-1 tw:left-1 tw: tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:text-xs">
            <div className="tw:size-6 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-300">
                <Check size={14} />
            </div>
        </div>
    )
}

const Image = ({ image, onDelete, isSelected, onSelect, onRefine }) => {
    const [hovered, setHovered] = useState(false)

    const isProcessing = image.status === 'processing'
    const isCompleted = image.status === 'completed'
    const width = {
        square: 130,
        vertical: 73,
        horizontal: 221,
    }[image.format]

    return (
        <div className="tw:flex tw:gap-4">
            <div
                className="tw:relative tw:flex tw:flex-col tw:gap-2 "
                style={{ maxWidth: width }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {isProcessing && <SkeletonLoader width={`${width}px`} height="130px" borderRadius="2px" />}

                {isCompleted && (
                    <div className="tw:relative tw:cursor-pointer">
                        <div onClick={onSelect}>
                            <HoverableImage
                                src={import.meta.env.VITE_S3_BUCKET_URL + image.url}
                                alt={image.name || 'Generated AI image'}
                                trigger="hover"
                                tooltipSize="medium"
                                className="tw:rounded-xs tw:w-full"
                                style={{ height: 130, width }}
                                thumbnailClassName="tw:transition-transform tw:duration-200 tw:hover:tw:scale-[1.02]"
                                expandedClassName="tw:rounded-xs tw:shadow-xl"
                                footer={
                                    <div className="tw:text-sm">
                                        <div className="tw:font-medium tw:truncate">{image.name}</div>
                                    </div>
                                }
                            />
                        </div>
                        {hovered && <DeleteButton width={width} image={image} onDelete={onDelete} hovered={hovered} />}
                        {hovered && onRefine && <RefineButton onRefine={onRefine} />}
                        {isSelected && <SelectionIcon />}
                    </div>
                )}

                <div className="tw:flex tw:flex-col tw:text-sm tw:overflow-hidden">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="tw:truncate">{image.name}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <span>{image.name}</span>
                        </TooltipContent>
                    </Tooltip>
                    <span className="tw:text-sm tw:text-gray-200">{image.format}</span>
                </div>
            </div>
        </div>
    )
}

export default Image
